<script setup>
/* eslint-disable vue/no-static-inline-styles */
import { computed, nextTick, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRouter } from 'vue-router';
import HomeHero from './HomeHero.vue';
import { HOME_PRIMARY_LINK_CLASS, homeToneClass } from './homeTones';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const channelRoute = subPage =>
  accountScopedRoute('settings_inboxes_page_channel', { sub_page: subPage });

const newInboxRoute = () => accountScopedRoute('settings_inbox_new');

const steps = computed(() => [
  {
    number: '01',
    title: t('OPERATIONAL_HOME.SETUP_GUIDE.STEPS.SETTINGS.TITLE'),
    description: t('OPERATIONAL_HOME.SETUP_GUIDE.STEPS.SETTINGS.DESCRIPTION'),
    icon: 'i-hugeicons-settings-01',
    tone: 'teal',
    action: {
      label: t('OPERATIONAL_HOME.SETUP_GUIDE.STEPS.SETTINGS.ACTION'),
      route: 'settings_inbox_new',
    },
  },
  {
    number: '02',
    title: t('OPERATIONAL_HOME.SETUP_GUIDE.STEPS.CHANNEL.TITLE'),
    description: t('OPERATIONAL_HOME.SETUP_GUIDE.STEPS.CHANNEL.DESCRIPTION'),
    icon: 'i-hugeicons-bubble-chat',
    tone: 'blue',
    channels: [
      {
        key: 'whatsapp',
        label: 'WhatsApp',
        icon: 'i-simple-icons-whatsapp',
        subPage: 'whatsapp',
      },
      {
        key: 'instagram',
        label: 'Instagram',
        icon: 'i-simple-icons-instagram',
        subPage: 'instagram',
      },
      {
        key: 'messenger',
        label: 'Messenger',
        icon: 'i-hugeicons-message-multiple-01',
        subPage: 'messenger',
      },
      {
        key: 'all',
        label: t('OPERATIONAL_HOME.SETUP_GUIDE.ALL_CHANNELS'),
        icon: 'i-hugeicons-dashboard-square-01',
        subPage: null,
      },
    ],
  },
  {
    number: '03',
    title: t('OPERATIONAL_HOME.SETUP_GUIDE.STEPS.AUTHORIZE.TITLE'),
    description: t('OPERATIONAL_HOME.SETUP_GUIDE.STEPS.AUTHORIZE.DESCRIPTION'),
    icon: 'i-hugeicons-security-check',
    tone: 'amber',
    action: {
      label: t('OPERATIONAL_HOME.SETUP_GUIDE.STEPS.AUTHORIZE.ACTION'),
      href: 'https://talkvia.com.br',
    },
  },
  {
    number: '04',
    title: t('OPERATIONAL_HOME.SETUP_GUIDE.STEPS.AGENTS.TITLE'),
    description: t('OPERATIONAL_HOME.SETUP_GUIDE.STEPS.AGENTS.DESCRIPTION'),
    icon: 'i-hugeicons-user-group',
    tone: 'grass',
    action: {
      label: t('OPERATIONAL_HOME.SETUP_GUIDE.STEPS.AGENTS.ACTION'),
      route: 'settings_inbox_new',
    },
  },
]);

const isTourActive = ref(false);
const activeTourStep = ref(0);
const spotlightStyle = ref({});
const tooltipStyle = ref({});

const activeStep = computed(() => steps.value[activeTourStep.value]);
const isLastStep = computed(
  () => activeTourStep.value === steps.value.length - 1
);

const updateSpotlight = async stepIndex => {
  await nextTick();
  const el = document.querySelector(`[data-guide-step="${stepIndex}"]`);
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const pad = 12;
  spotlightStyle.value = {
    top: `${rect.top - pad}px`,
    left: `${rect.left - pad}px`,
    width: `${rect.width + pad * 2}px`,
    height: `${rect.height + pad * 2}px`,
    borderRadius: '16px',
  };
  const tooltipTop = rect.bottom + pad + 16;
  const tooltipLeft = Math.max(16, rect.left - 60);
  tooltipStyle.value = {
    top: `${tooltipTop}px`,
    left: `${tooltipLeft}px`,
    maxWidth: 'min(360px, calc(100vw - 32px))',
  };
};

const startTour = async () => {
  isTourActive.value = true;
  activeTourStep.value = 0;
  await updateSpotlight(0);
};

const nextStep = async () => {
  if (activeTourStep.value < steps.value.length - 1) {
    activeTourStep.value += 1;
    await updateSpotlight(activeTourStep.value);
  } else {
    isTourActive.value = false;
  }
};

const prevStep = async () => {
  if (activeTourStep.value > 0) {
    activeTourStep.value -= 1;
    await updateSpotlight(activeTourStep.value);
  }
};

const closeTour = () => {
  isTourActive.value = false;
};

const handleKeydown = e => {
  if (!isTourActive.value) return;
  if (e.key === 'ArrowRight') nextStep();
  if (e.key === 'ArrowLeft') prevStep();
  if (e.key === 'Escape') closeTour();
};

const navigateStep = step => {
  if (step.action?.route) {
    router.push(accountScopedRoute(step.action.route));
  } else if (step.action?.href) {
    window.open(step.action.href, '_blank', 'noopener');
  }
};

const navigateChannel = channel => {
  if (channel.subPage) {
    router.push(channelRoute(channel.subPage));
  } else {
    router.push(newInboxRoute());
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', handleKeydown);
  onUnmounted(() => window.removeEventListener('keydown', handleKeydown));
}
</script>

<template>
  <div class="h-full overflow-y-auto bg-n-background">
    <div class="mx-auto w-full max-w-[90rem] px-4 pb-12 sm:px-6 lg:px-8">
      <HomeHero
        :eyebrow="t('OPERATIONAL_HOME.SETUP_GUIDE.EYEBROW')"
        :title="t('OPERATIONAL_HOME.SETUP_GUIDE.TITLE')"
        :description="t('OPERATIONAL_HOME.SETUP_GUIDE.SUBTITLE')"
        icon="i-hugeicons-inbox"
      >
        <button
          type="button"
          :class="HOME_PRIMARY_LINK_CLASS"
          @click="startTour"
        >
          <span class="i-hugeicons-play size-4" />
          {{ t('OPERATIONAL_HOME.SETUP_GUIDE.START_TOUR') }}
        </button>
      </HomeHero>

      <ol
        class="m-0 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 xl:grid-cols-4"
      >
        <li
          v-for="(step, idx) in steps"
          :key="step.number"
          :data-guide-step="idx"
          class="flex flex-col gap-3 rounded-2xl border border-n-weak bg-n-solid-1 p-5 shadow-sm dark:border-n-strong"
        >
          <div class="flex items-start justify-between">
            <span
              class="grid size-10 place-items-center rounded-xl"
              :class="homeToneClass(step.tone)"
              aria-hidden="true"
            >
              <span :class="step.icon" class="size-5" />
            </span>
            <span
              class="rounded-md bg-n-solid-2 px-2 py-0.5 text-xs font-semibold tabular-nums text-n-slate-11"
            >
              {{ step.number }}
            </span>
          </div>
          <div>
            <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ step.title }}
            </h3>
            <p class="mb-0 mt-1 text-sm leading-5 text-n-slate-11">
              {{ step.description }}
            </p>
          </div>

          <div v-if="step.channels" class="mt-auto flex flex-wrap gap-2 pt-1">
            <button
              v-for="channel in step.channels"
              :key="channel.key"
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak px-2.5 py-1.5 text-xs font-medium text-n-slate-12 transition-colors hover:border-n-brand hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand dark:border-n-strong"
              @click="navigateChannel(channel)"
            >
              <span :class="channel.icon" class="size-3.5" />
              {{ channel.label }}
            </button>
          </div>

          <div v-else-if="step.action" class="mt-auto pt-1">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak px-2.5 py-1.5 text-xs font-medium text-n-slate-12 transition-colors hover:border-n-brand hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand dark:border-n-strong"
              @click="navigateStep(step)"
            >
              {{ step.action.label }}
              <span
                :class="
                  step.action.href
                    ? 'i-hugeicons-link-square-02'
                    : 'i-hugeicons-arrow-right-01'
                "
                class="size-3.5"
              />
            </button>
          </div>
        </li>
      </ol>
    </div>
  </div>

  <Teleport to="body">
    <div v-if="isTourActive" class="fixed inset-0 z-50" @click.self="closeTour">
      <div
        class="pointer-events-none fixed border-2 border-n-brand motion-safe:transition-all motion-safe:duration-300"
        style="box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6)"
        :style="spotlightStyle"
      />

      <div
        class="fixed z-[51] rounded-2xl border border-n-weak bg-n-solid-1 p-4 shadow-xl dark:border-n-strong"
        role="dialog"
        aria-modal="true"
        :aria-label="activeStep.title"
        :style="tooltipStyle"
      >
        <div class="mb-2 flex items-center justify-between gap-4">
          <span class="text-xs font-semibold text-n-brand">
            {{
              t('OPERATIONAL_HOME.SETUP_GUIDE.STEP_PROGRESS', {
                current: activeTourStep + 1,
                total: steps.length,
              })
            }}
          </span>
          <button
            type="button"
            class="grid size-7 place-items-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            :aria-label="t('OPERATIONAL_HOME.SETUP_GUIDE.CLOSE_TOUR')"
            @click="closeTour"
          >
            <span class="i-hugeicons-cancel-01 size-4" />
          </button>
        </div>
        <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
          {{ activeStep.title }}
        </h4>
        <p class="mb-0 mt-1 text-sm text-n-slate-11">
          {{ activeStep.description }}
        </p>
        <div class="mt-4 flex items-center gap-2">
          <button
            v-if="activeTourStep > 0"
            type="button"
            class="rounded-lg border border-n-weak px-3 py-1.5 text-xs font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-2 dark:border-n-strong"
            @click="prevStep"
          >
            {{ t('OPERATIONAL_HOME.SETUP_GUIDE.PREVIOUS') }}
          </button>
          <button
            type="button"
            class="rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white transition hover:brightness-110"
            @click="nextStep"
          >
            {{
              isLastStep
                ? t('OPERATIONAL_HOME.SETUP_GUIDE.FINISH')
                : t('OPERATIONAL_HOME.SETUP_GUIDE.NEXT')
            }}
          </button>
          <div class="ml-auto flex gap-1.5" aria-hidden="true">
            <span
              v-for="(_, i) in steps"
              :key="i"
              class="size-1.5 rounded-full"
              :class="i === activeTourStep ? 'bg-n-brand' : 'bg-n-slate-6'"
            />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
